import { useMemo, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { useEstablishment } from '../contexts/EstablishmentContext';
import { Role } from '../constants/roles';
import { 
  getCachedPermissions, 
  setCachedPermissions
} from '../utils/permissionCache';

export interface EstablishmentPermissionResult {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  canViewRoute: (route: string) => boolean;
  currentPermissions: string[];
  currentRoles: string[];
  isLoading: boolean;
}

/**
 * Hook for checking permissions in the context of the current establishment
 * @returns Object with permission checking utilities
 */
export const useEstablishmentPermissions = (): EstablishmentPermissionResult => {
  const { user, permissions: globalPermissions, isLoading: authLoading } = useAuth();
  const { currentEstablishment, loading: establishmentLoading } = useEstablishment();

  // Try to get cached permissions first
  const cachedData = useMemo(() => {
    if (!user?.id || !currentEstablishment?.id) return null;
    return getCachedPermissions(user.id, currentEstablishment.id);
  }, [user?.id, currentEstablishment?.id]);

  // Get permissions for current establishment
  const establishmentPermissions = useMemo(() => {
    // console.log('[useEstablishmentPermissions] Debug:', {
    //   currentEstablishment,
    //   globalPermissions,
    //   cachedData,
    //   establishmentPermissions: currentEstablishment?.permissions
    // });

    // TEMPORARY: Skip cache to debug
    // if (cachedData) {
    //   return cachedData.permissions;
    // }

    if (!currentEstablishment) {
      // If no establishment selected, use global permissions
      return globalPermissions || [];
    }

    // Use establishment-specific permissions if available (with actual permissions)
    // IMPORTANT: Check for length > 0 because empty array [] is truthy in JavaScript
    // This ensures we fallback to globalPermissions when establishment.permissions is empty
    const estPermissions = currentEstablishment.permissions;
    if (estPermissions && estPermissions.length > 0) {
      return estPermissions;
    }

    // Fallback to global permissions
    return globalPermissions || [];
  }, [currentEstablishment, globalPermissions]);

  // Get roles for current establishment
  const establishmentRoles = useMemo(() => {
    // Use cache if available and fresh
    if (cachedData) {
      return cachedData.roles;
    }

    if (!currentEstablishment) {
      // If no establishment selected, use user's global roles
      return user?.roles || [];
    }
    
    // Use establishment-specific roles
    return currentEstablishment.roles || [];
  }, [currentEstablishment, user, cachedData]);

  // Update cache when permissions or roles change
  useEffect(() => {
    if (user?.id && currentEstablishment?.id && !cachedData) {
      // Cache the permissions and roles
      setCachedPermissions(
        user.id,
        currentEstablishment.id,
        establishmentPermissions,
        establishmentRoles as string[]
      );
    }
  }, [user?.id, currentEstablishment?.id, establishmentPermissions, establishmentRoles, cachedData]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    // console.log('[hasPermission] Checking:', {
    //   permission,
    //   establishmentPermissions,
    //   currentEstablishment: currentEstablishment?.name,
    //   user: user?.email
    // });

    if (!permission) return false;
    
    // TEMPORARY: Skip cache to debug
    // Check cache first
    // if (user?.id && currentEstablishment?.id) {
    //   const cachedResult = permissionCache.getPermissionCheck(
    //     user.id,
    //     currentEstablishment.id,
    //     permission
    //   );
      
    //   if (cachedResult !== null) {
    //     return cachedResult;
    //   }
    // }
    
    const result = establishmentPermissions.some(perm => {
      // Exact match
      if (perm === permission) return true;
      
      // Check if user has a parent permission (e.g., 'maintenance' covers 'maintenance.view')
      if (permission.startsWith(perm + '.')) return true;
      
      // Check if user has a wildcard permission (e.g., 'maintenance.*' covers 'maintenance.view')
      if (perm.endsWith('.*')) {
        const permBase = perm.slice(0, -2);
        return permission.startsWith(permBase + '.');
      }
      
      return false;
    });
    
    // TEMPORARY: Skip caching completely to avoid stale data
    // Cache the result
    // if (user?.id && currentEstablishment?.id) {
    //   permissionCache.setPermissionCheck(
    //     user.id,
    //     currentEstablishment.id,
    //     permission,
    //     result
    //   );
    // }
    
    return result;
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(perm => hasPermission(perm));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(perm => hasPermission(perm));
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: Role): boolean => {
    return establishmentRoles.includes(role);
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: Role[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  /**
   * Check if user can view a specific route based on permissions
   */
  const canViewRoute = (route: string): boolean => {
    // Remove trailing slash
    const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;
    
    // Check for exact route permission
    if (hasPermission(`route.${normalizedRoute}`)) return true;
    
    // Check for path-based permissions
    const pathSegments = normalizedRoute.split('/').filter(Boolean);
    
    // Try different permission patterns
    const first = pathSegments[0] ?? '';
    const possiblePermissions = [
      pathSegments.join('.') + '.view',
      pathSegments.join('.') + '.access',
      pathSegments.join('.'),
      first + '.view',
      first + '.access',
      first,
    ];
    
    return hasAnyPermission(possiblePermissions);
  };

  // Short initial grace period to avoid flicker right after login
  const [graceOver, setGraceOver] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGraceOver(true), 800);
    return () => clearTimeout(t);
  }, []);

  // Treat as loading while token exists but we haven't resolved permissions yet
  const tokenPresent = typeof window !== 'undefined' ? !!localStorage.getItem('mep_token') : false;
  const permissionsResolved = (currentEstablishment?.permissions?.length || 0) > 0 || (globalPermissions?.length || 0) > 0;
  const derivedLoading = authLoading || establishmentLoading || (tokenPresent && !permissionsResolved && !graceOver);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    canViewRoute,
    currentPermissions: establishmentPermissions,
    currentRoles: establishmentRoles as Role[],
    isLoading: derivedLoading,
  };
};

// Convenience hook for single permission check
export const useHasPermission = (permission: string): boolean => {
  const { hasPermission } = useEstablishmentPermissions();
  return hasPermission(permission);
};

// Convenience hook for role check
export const useHasRole = (role: Role): boolean => {
  const { hasRole } = useEstablishmentPermissions();
  return hasRole(role);
};

// Convenience hook for route permission check
export const useCanViewRoute = (route: string): boolean => {
  const { canViewRoute } = useEstablishmentPermissions();
  return canViewRoute(route);
};
